import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AvatarDisplay from './AvatarDisplay';
import AvatarWithTooltip from './AvatarWithTooltip';
import { postService } from '../services/postService';
import { useAnnouncements } from '../hooks/useAnnouncements'; // <-- NEW

const RightSidebar = () => {
  const { isAuthenticated, ensName, getAuthHeaders } = useContext(AuthContext);
  const [featuredUsers, setFeaturedUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [userError, setUserError] = useState(null);
  const [stats, setStats] = useState(null);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [trendingError, setTrendingError] = useState(null);
  
  // NEW: announcements hook
  const { announcements, loading: announcementsLoading, error: announcementsError } = useAnnouncements();
  
  const navigate = useNavigate();

  // Fetch trending posts (most liked in last 7 days)
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        setTrendingLoading(true);
        const headers = getAuthHeaders();
        const res = await fetch('/api/posts/trending?limit=5', { headers });
        const data = await res.json();
        if (data.success) {
          setTrendingPosts(data.posts);
        } else {
          setTrendingError('Failed to load trending');
        }
      } catch (err) {
        setTrendingError(err.message);
      } finally {
        setTrendingLoading(false);
      }
    };
    fetchTrending();
  }, [getAuthHeaders]);

  // Helper to truncate post content
  const truncate = (text, max = 50) => {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '…' : text;
  };

  // Simple engagement-based trend indicator
  const getTrend = (post) => {
    const total = post.likes_count + post.reposts_count + post.comment_count;
    if (total > 20) return 'hot';
    if (total > 10) return 'rising';
    return 'normal';
  };

  const fetchFeaturedUsers = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      setUserError(null);

    const response = await fetch('https://zpost.ai/api/ens/featured-users', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('zhat_token')}` }
    });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 RAW featured users data:', data);

      if (data.success && data.users && data.users.length > 0) {
        const formattedUsers = data.users.map(user => ({
          ens: user.ens,
          address: user.address,
          bio: user.bio,
          avatarUrl: user.avatarUrl,
          posts: user.posts || 0,
          followers: user.followers || 0,
          following: user.following || 0,
          isFollowing: user.is_following || false,
          isRealUser: true
        }));

        setFeaturedUsers(formattedUsers);
      } else {
        throw new Error('No real users found, using placeholders');
      }
    } catch (err) {
      console.log('Using fallback user data:', err.message);
      setUserError(err.message);
      setFeaturedUsers(getPlaceholderUsers());
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const fetchCommunityStats = useCallback(async () => {
    try {
      const response = await fetch('/api/ens/stats');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) {
          setStats({
            total_users: data.stats.totalUsers.toLocaleString(),
            active_users: data.stats.activeUsers.toLocaleString(),
            total_posts: data.stats.totalPosts.toLocaleString(),
            total_messages: data.stats.totalMessages.toLocaleString()   // new key
          });
        } else {
          throw new Error('Invalid stats data');
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      console.log('Could not fetch community stats, using defaults:', err);
      setStats({
        total_users: '1.2k',
        active_users: '356',
        total_posts: '4.8k',
        total_messages: '2.3k'          // fallback value
      });
    }
  }, []);

  // Format time ago for last login
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'recently';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays/7)}w ago`;
  };

  // Placeholder users (fallback)
  const getPlaceholderUsers = () => [
    {
      ens: 'olga.zpost.eth',
      address: '0x1234567890123456789012345678901234567890',
      bio: 'Early adopter & community leader',
      status: 'active',
      posts: 142,
      followers: 50,
      avatarUrl: null
    },
    {
      ens: 'founder.zpost.eth',
      address: '0x1234567890123456789012345678901234567891',
      bio: 'Building the future of social',
      status: 'active',
      posts: 89,
      followers: 120,
      avatarUrl: null
    },
    {
      ens: 'alice.zpost.eth',
      address: '0x1234567890123456789012345678901234567892',
      bio: 'First 100 member | Web3 enthusiast',
      status: 'idle',
      posts: 56,
      followers: 30,
      avatarUrl: null
    },
    {
      ens: 'mike.zpost.eth',
      address: '0x1234567890123456789012345678901234567893',
      bio: 'DeFi strategist & writer',
      status: 'active',
      posts: 203,
      followers: 200,
      avatarUrl: null
    }
  ];

  // Handle follow action
  const handleFollow = async (ens) => {
    console.log(`Followed ${ens}`);
    alert(`Following ${ens}`);
  };

  const handleSearchDomain = (domain) => {
    if (!domain.trim()) return;
    navigate(`/ens-search?q=${encodeURIComponent(domain.trim())}`);
  };

  // Navigate to IdentityDashboard (the authenticated ENS app)
  const goToIdentityDashboard = () => {
    navigate('/home/identity');
  };

  // Initial fetch
  useEffect(() => {
    fetchFeaturedUsers();
    fetchCommunityStats();
  }, [fetchFeaturedUsers, fetchCommunityStats]);

    const handleToggleFollow = async (user) => {
    setFeaturedUsers(prev => prev.map(u =>
      u.address === user.address
        ? { ...u, isFollowing: !u.isFollowing, followers: u.isFollowing ? u.followers - 1 : u.followers + 1 }
        : u
    ));

    try {
      if (user.isFollowing) {
        await postService.unfollowUser(user.address);
      } else {
        await postService.followUser(user.address);
      }
    } catch (error) {
      console.error('Follow action failed:', error);
      setFeaturedUsers(prev => prev.map(u =>
        u.address === user.address
          ? { ...u, isFollowing: !u.isFollowing, followers: u.isFollowing ? u.followers + 1 : u.followers - 1 }
          : u
      ));
    }
  };

  return (
    <aside className="zpost-right-sidebar">
      <div className="sidebar-sticky">
        <div className="right-sidebar-content">
          
          {/* Professional Search Bar */}
          <div className="right-sidebar-search" style={{ marginBottom: '1.75rem' }}>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-search" style={{
                position: 'absolute',
                left: '0.3rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                lineHeight: 1,
                pointerEvents: 'none'
              }}></i>
              <input 
                type="text" 
                placeholder="Search ENS domains..." 
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem 0.875rem 3rem',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '9999px',
                  color: 'var(--text-light)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--gold)';
                  e.target.style.boxShadow = '0 0 0 3px var(--gold-shadow)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    handleSearchDomain(e.target.value);
                  }
                }}
              />
            </div>
          </div>
          
          {/* Stats Card */}
          {stats && (
            <div className="right-sidebar-section" style={{ marginBottom: '1.75rem' }}>
              <div className="section-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: 0 }}>
                  <i className="fas fa-chart-line" style={{ color: 'var(--gold)', fontSize: '1rem' }}></i>
                  Community Stats
                </h3>
              </div>
              <div className="section-content">
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  padding: '1rem'
                }}>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(255,215,0,0.05)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.25rem' }}>
                      {stats.total_users}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Total Users
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(255,215,0,0.05)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.25rem' }}>
                      {stats.active_users}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Active Users
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(255,215,0,0.05)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.25rem' }}>
                      {stats.total_posts}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Total Posts
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.75rem', background: 'rgba(255,215,0,0.05)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.25rem' }}>
                      {stats.total_messages}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Total Messages
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Trending Section - Dynamic from API */}
          <div className="right-sidebar-section" style={{ marginBottom: '1.75rem' }}>
            <div className="section-header">
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                margin: 0
              }}>
                <i className="fas fa-fire" style={{ 
                  color: 'var(--gold)', 
                  fontSize: '1rem' 
                }}></i>
                Trending Now
              </h3>
            </div>
            <div className="section-content">
              {trendingLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Loading trending posts...
                </div>
              ) : trendingError ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#ff4444' }}>
                  Failed to load trending
                </div>
              ) : trendingPosts.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No trending posts yet
                </div>
              ) : (
                trendingPosts.map((post, index) => {
                  const trend = getTrend(post);
                  return (
                    <div
                      key={post.id}
                      className="trend-item"
                      onClick={() => navigate(`/post/${post.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '0.875rem 1.25rem',
                        borderBottom: index < trendingPosts.length - 1 ? '1px solid var(--border)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                        e.currentTarget.style.paddingLeft = '1.5rem';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.paddingLeft = '1.25rem';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{ color: 'var(--text-light)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {post.author?.ens || post.author?.displayName || 'Anonymous'}
                          </span>
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '0.1rem 0.4rem',
                            borderRadius: '4px',
                            background: trend === 'hot' ? 'rgba(255, 69, 0, 0.2)' :
                                        trend === 'rising' ? 'rgba(255, 215, 0, 0.2)' :
                                        'rgba(128, 128, 128, 0.2)',
                            color: trend === 'hot' ? '#FF4500' :
                                   trend === 'rising' ? 'var(--gold)' :
                                   'var(--text-secondary)',
                            border: '1px solid',
                            borderColor: trend === 'hot' ? '#FF4500' :
                                         trend === 'rising' ? 'var(--gold)' :
                                         'var(--border)'
                          }}>
                            {trend}
                          </span>
                        </div>
                        <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: '0.25rem 0', wordBreak: 'break-word' }}>
                          {truncate(post.content)}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          <span><i className="fas fa-heart"></i> {post.likes_count}</span>
                          <span><i className="fas fa-retweet"></i> {post.reposts_count}</span>
                          <span><i className="fas fa-comment"></i> {post.comment_count}</span>
                        </div>
                      </div>
                      <i className="fas fa-chevron-right" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginLeft: '0.5rem' }}></i>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          
          {/* Who to Follow - Professional with Real Users */}
          <div className="right-sidebar-section" style={{ marginBottom: '1.75rem' }}>
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}>
                <h3 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: 0
                }}>
                  <i className="fas fa-user-plus" style={{
                    color: 'var(--dark-red)',
                    fontSize: '1rem'
                  }}></i>
                  Who to Follow
                </h3>
              </div>
            </div>
            
            <div className="section-content">
              {isLoadingUsers ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div className="loading-spinner" style={{ 
                    margin: '0 auto 1rem',
                    border: '2px solid var(--border)',
                    borderTop: '2px solid var(--gold)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ fontSize: '0.8rem' }}>
                    Loading community members...
                  </p>
                </div>
              ) : (
                <>
                  {featuredUsers.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: 'var(--text-secondary)',
                      border: '1px dashed var(--border)',
                      borderRadius: '12px',
                      margin: '1rem',
                      background: 'rgba(255, 215, 0, 0.05)'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--gold)' }}>👤</div>
                      <h4 style={{ color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                        Be the First!
                      </h4>
                      <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                        No community members yet. Be the first to post!
                      </p>
                      <Link
                        to="/home/identity"
                        className="btn"
                        style={{
                          background: 'var(--gold)',
                          color: 'var(--black)',
                          border: 'none',
                          padding: '0.5rem 1.5rem',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          display: 'inline-block',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#FFE44D';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--gold)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        Claim Your .zpost.eth
                      </Link>
                    </div>
                  ) : (
                    featuredUsers.map((user, index) => (
                      <div
                        key={user.ens}
                        className="follow-suggestion"
                        style={{
                          padding: '0.875rem 1.25rem',
                          borderBottom: index < featuredUsers.length - 1 ? '1px solid var(--border)' : 'none',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                          e.currentTarget.style.paddingLeft = '1.5rem';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.paddingLeft = '1.25rem';
                        }}
                        onClick={() => navigate(`/profile/${user.address || user.eth_address}`)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                          <AvatarDisplay
                            ensName={user.ens}
                            address={user.address || user.eth_address}
                            avatarUrl={user.avatarUrl}
                            size={40}
                            showStatus={true}
                            isOnline={user.status === 'active' || user.status === 'online'}
                          />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: 'var(--text-light)',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              marginBottom: '0.125rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {user.ens}
                            </div>
                            <div style={{
                              color: 'var(--text-secondary)',
                              fontSize: '0.8rem',
                              marginBottom: '0.25rem',
                              lineHeight: '1.2',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {user.bio}
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              color: 'var(--text-secondary)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span>
                                <i className="fas fa-message" style={{ marginRight: '0.25rem' }}></i>
                                {user.posts} posts
                              </span>
                              <span>•</span>
                              <span>
                                <i className="fas fa-user-friends" style={{ marginRight: '0.25rem' }}></i>
                                {user.followers} followers
                              </span>
                            </div>
                          </div>

                        <button
                          style={{
                            background: user.isFollowing ? 'var(--dark-red)' : 'var(--gold)',
                            color: 'var(--black)',
                            border: 'none',
                            padding: '0.375rem 0.875rem',
                            borderRadius: '9999px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            minWidth: '70px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFollow(user);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = user.isFollowing ? '#600000' : '#FFE44D';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = user.isFollowing ? 'var(--dark-red)' : 'var(--gold)';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          {user.isFollowing ? 'Unfollow' : 'Follow'}
                        </button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {/* Directory link */}
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '1rem',
                    borderTop: '1px solid var(--border)'
                  }}>
                    <button
                      onClick={goToIdentityDashboard}
                      style={{
                        background: 'transparent',
                        color: 'var(--gold)',
                        border: '1px solid var(--gold)',
                        padding: '0.5rem 1.5rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 215, 0, 0.1)';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <i className="fas fa-search"></i>
                      Explore ENS Directory
                    </button>
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--text-secondary)',
                      marginTop: '0.5rem'
                    }}>
                      Look up any ENS domain on Ethereum
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Announcements - Professional Card (NOW DYNAMIC) */}
          <div className="right-sidebar-section" style={{ marginBottom: '1.75rem' }}>
            <div className="section-header">
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                margin: 0
              }}>
                <i className="fas fa-bullhorn" style={{ 
                  color: 'var(--gold)', 
                  fontSize: '1rem' 
                }}></i>
                Announcements
              </h3>
            </div>
            <div className="section-content">
              {announcementsLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Loading announcements...
                </div>
              ) : announcementsError ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#ff4444' }}>
                  Failed to load announcements
                </div>
              ) : announcements.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No announcements yet
                </div>
              ) : (
                announcements.map((announcement, index) => (
                  <div 
                    key={announcement.id}
                    className="news-card"
                    style={{
                      margin: '0.75rem 1.25rem',
                      padding: '0.875rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/post/${announcement.id}`)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--gold)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px var(--gold-shadow)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.1rem 0.5rem',
                        borderRadius: '4px',
                        background: 'rgba(255, 215, 0, 0.1)',
                        color: 'var(--gold)',
                        border: '1px solid var(--gold)'
                      }}>
                        {announcement.category}
                      </span>
                      <span style={{ 
                        color: 'var(--text-secondary)', 
                        fontSize: '0.75rem' 
                      }}>
                        {formatTimeAgo(announcement.date)}
                      </span>
                    </div>
                    <div style={{ 
                      color: 'var(--text-light)', 
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      marginBottom: '0.25rem'
                    }}>
                      {announcement.title}
                    </div>
                    <div style={{ 
                      color: 'var(--text-secondary)', 
                      fontSize: '0.8rem',
                      lineHeight: '1.3'
                    }}>
                      {announcement.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Quick Actions - Professional */}
          <div className="right-sidebar-section" style={{ marginBottom: '1.75rem' }}>
            <div className="section-header">
              <h3 style={{ margin: 0 }}>Quick Actions</h3>
            </div>
            <div className="section-content">
              <Link 
                to="/zhat" 
                target="_blank"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1.25rem',
                  color: 'var(--text-light)',
                  textDecoration: 'none',
                  borderBottom: '1px solid var(--border)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                  e.currentTarget.style.paddingLeft = '1.5rem';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-comments" style={{ 
                    color: 'var(--gold)', 
                    fontSize: '0.9rem',
                    width: '20px'
                  }}></i>
                  <span style={{ fontSize: '0.9rem' }}>Open Zhat</span>
                </div>
                <i className="fas fa-external-link-alt" style={{ 
                  fontSize: '0.8rem', 
                  color: 'var(--text-secondary)' 
                }}></i>
              </Link>
              
              <Link 
                to="/home/identity"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1.25rem',
                  color: 'var(--text-light)',
                  textDecoration: 'none',
                  borderBottom: '1px solid var(--border)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                  e.currentTarget.style.paddingLeft = '1.5rem';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-id-card" style={{ 
                    color: 'var(--gold)', 
                    fontSize: '0.9rem',
                    width: '20px'
                  }}></i>
                  <span style={{ fontSize: '0.9rem' }}>Get .zpost.eth</span>
                </div>
                <i className="fas fa-chevron-right" style={{ 
                  fontSize: '0.8rem', 
                  color: 'var(--text-secondary)' 
                }}></i>
              </Link>
              
              <Link 
                to="/home/wallet"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 1.25rem',
                  color: 'var(--text-light)',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 215, 0, 0.05)';
                  e.currentTarget.style.paddingLeft = '1.5rem';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className="fas fa-wallet" style={{ 
                    color: 'var(--gold)', 
                    fontSize: '0.9rem',
                    width: '20px'
                  }}></i>
                  <span style={{ fontSize: '0.9rem' }}>Wallet</span>
                </div>
                <i className="fas fa-chevron-right" style={{ 
                  fontSize: '0.8rem', 
                  color: 'var(--text-secondary)' 
                }}></i>
              </Link>
            </div>
          </div>
          
          {/* Professional Footer */}
          <div className="right-sidebar-footer">
            <div className="footer-links" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              marginBottom: '0.875rem'
            }}>
              <Link
                to="/help"
                className="footer-link"
                style={{
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '0.8rem',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--gold)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
              >
                Help Center
              </Link>
              <Link
                to="/privacy"
                className="footer-link"
                style={{
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '0.8rem',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--gold)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
              >
                Privacy
              </Link>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: '0.875rem'
            }}>
              <i className="fas fa-bolt" style={{ color: 'var(--gold)', fontSize: '0.9rem' }}></i>
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '0.75rem',
                textAlign: 'center'
              }}>
                Powered by .zpost.eth domains
              </span>
              <i className="fas fa-shield-alt" style={{ color: 'var(--gold)', fontSize: '0.9rem' }}></i>
            </div>

            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '0.7rem',
              textAlign: 'center',
              opacity: 0.7
            }}>
              © 2025 Zpost. Your sovereign identity, forever.
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default RightSidebar;
